import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function AwsPage(): Node {
  return html`
    <div>
      <h1>Deploy to AWS (S3 + CloudFront)</h1>
      <p>Host your onefold app on S3 with CloudFront as the CDN. Configure a custom error response to enable SPA fallback with <strong>path-based routing</strong>.</p>

      <h2>Option A: Path-Based Routing (Recommended)</h2>
      <p>Use CloudFront's custom error response to serve <code>index.html</code> for all 404s.</p>

      <h3>1. Create an S3 Bucket</h3>
      ${CodeBlock(`aws s3 mb s3://my-onefold-app --region us-east-1`, 'bash')}

      <h3>2. Upload the Build</h3>
      ${CodeBlock(`npm run build
aws s3 sync dist/ s3://my-onefold-app --delete`, 'bash')}

      <h3>3. Configure CloudFront</h3>
      <p>Create a CloudFront distribution with the S3 bucket as origin. Add a custom error response:</p>

      <table>
        <tr><th>Setting</th><th>Value</th></tr>
        <tr><td>HTTP Error Code</td><td>403</td></tr>
        <tr><td>Response Page Path</td><td><code>/index.html</code></td></tr>
        <tr><td>Response Code</td><td>200</td></tr>
      </table>

      <p>Add a second rule for 404:</p>
      <table>
        <tr><th>Setting</th><th>Value</th></tr>
        <tr><td>HTTP Error Code</td><td>404</td></tr>
        <tr><td>Response Page Path</td><td><code>/index.html</code></td></tr>
        <tr><td>Response Code</td><td>200</td></tr>
      </table>

      ${Callout('S3 returns 403 (not 404) for paths that do not exist when using OAI/OAC. Configure both 403 and 404 error responses.')}

      <h3>4. Router Configuration</h3>
      ${CodeBlock(`import { Router, mount } from 'onefold';

// Path-based (default) — works with CloudFront error response
const app = Router([
  { path: '/', view: () => Home() },
  { path: '/about', view: () => About() },
], () => NotFound());

mount(app, document.getElementById('app')!);`)}

      <h2>Option B: Hash-Based Routing (No CloudFront)</h2>
      <p>If you're serving directly from S3 static website hosting without CloudFront, use hash-based routing:</p>

      ${CodeBlock(`import { configureRouter, Router, navigate, mount } from 'onefold';

configureRouter({ hash: true });

const app = Router([...routes], () => NotFound());
mount(app, document.getElementById('app')!);

// Intercept internal links
document.addEventListener('click', (e) => {
  const anchor = (e.target as HTMLElement).closest('a');
  if (!anchor) return;
  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('http') || href.startsWith('#') || href.startsWith('mailto:')) return;
  e.preventDefault();
  navigate(href);
});

if (!location.hash || location.hash === '#/') {
  navigate('/');
}`)}

      <p>And use relative asset paths in <code>index.html</code>:</p>
      ${CodeBlock(`<link rel="stylesheet" href="./style.css" />
<script type="module" src="./app.js"></script>`, 'html')}

      <h2>AWS CLI Deploy Script</h2>
      ${CodeBlock(`#!/bin/bash
# deploy.sh
npm run build
aws s3 sync dist/ s3://my-onefold-app --delete \\
  --cache-control "public, max-age=31536000, immutable" \\
  --exclude "index.html"

# index.html should not be cached long-term
aws s3 cp dist/index.html s3://my-onefold-app/index.html \\
  --cache-control "public, max-age=0, must-revalidate"

# Invalidate CloudFront cache
aws cloudfront create-invalidation \\
  --distribution-id YOUR_DIST_ID \\
  --paths "/*"`, 'bash')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/deployment/docker">Docker / Node</a> — containerized deployment with a server</li>
        <li><a href="/deployment/github-pages">GitHub Pages</a> — free static hosting</li>
      </ul>
    </div>
  `;
}
